import React, { useEffect, useState, useRef } from "react";
import Select from "react-select";
import IsLogin from "../../../Common/IsLogin";
import ReactDataTable from "../../../Common/ReactDataTable";
import { useLocation } from "react-router-dom";
import Http from "../../../Common/Http";
import DatatableFunc from "../../../Common/DatatableFunc";
import Alert from "../../../Common/Alert";

const VendorLedger = () => {
  const location = useLocation();
  const printRef = useRef(null);
  const [vendorData, setVendorData] = useState([]);
  const [vendorsList, setVendorsList] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [alert, setAlert] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [summary, setSummary] = useState({
    previousDue: 0,
    grnTotal: 0,
    payment: 0,
    return: 0,
    balance: 0
  });

  // Define cols here - this is important!
  const cols = [
    { field: "date", header: "Date" },
    { field: "type", header: "Type" },
    { field: "amount", header: "Amount" },
    { field: "balance", header: "Balance" }
  ];

  const getCurrentMonthDates = () => {
    const today = new Date();
    // Get first day of current month
    const firstDayOfCurrentMonth = new Date(today.getFullYear(), today.getMonth(), 1);

    const formatDate = (date) => {
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${year}-${month}-${day}`; // Format as YYYY-MM-DD for HTML date inputs
    };

    return {
      firstDate: formatDate(firstDayOfCurrentMonth), // First day of current month in YYYY-MM-DD format
      todayDate: formatDate(today) // Today's date in YYYY-MM-DD format
    };
  };

  const fetchVendors = async () => {
    try {
      const res = await Http.get('/vendor');
      const options = res.data.map(vendor => ({
        value: vendor.id,
        label: vendor.name
      }));
      setVendorsList(options);

      // Set the first vendor as selected by default
      if (options && options.length > 0) {
        setSelectedVendor(options[0]);
      }
    } catch (error) {
      console.error("Error fetching vendors:", error);
      showAlert("Failed to load vendors", "danger");
    }
  };

  const fetchLedgerData = async () => {
    if (!selectedVendor) return;

    setLoading(true);
    try {
      const res = await Http.get(`/accounts/get-vendor-ledger-summary/${selectedVendor.value}?start_date=${startDate}&end_date=${endDate}`);
      setVendorData(res.data.transactions || []);
      setSummary({
        previousDue: res.data.summary.previousDue || 0,
        grnTotal: res.data.summary.grnTotal || 0,
        payment: res.data.summary.payment || 0,
        return: res.data.summary.return || 0,
        balance: res.data.summary.balance || 0
      });
    } catch (error) {
      console.error("Fetch error:", error);
      showAlert("Failed to load ledger data", "danger");
    } finally {
      setLoading(false);
    }
  };

  const showAlert = (message, type) => {
    setAlert({
      message: message,
      type: type,
    });
    setTimeout(() => {
      setAlert(null);
    }, 4000);
  };

  useEffect(() => {
    const dates = getCurrentMonthDates();
    setStartDate(dates.firstDate);
    setEndDate(dates.todayDate);
    DatatableFunc();
    fetchVendors(); // This will now set the first vendor as selected
  }, []);

  useEffect(() => {
    if (startDate && endDate && selectedVendor) {
      fetchLedgerData();
    }
  }, [startDate, endDate, selectedVendor]);

  useEffect(() => {
    if (!location.state) {
      return;
    }
    showAlert(location.state.message, location.state.type);
  }, [location]);

  const handleVendorChange = (selected) => {
    setSelectedVendor(selected);
  };

  // Clean print: generate standalone HTML without any UI controls or overlays
  const handlePrint = () => {
    // Safer number parsing to avoid NaN for values like "1,234.56" or "(123.45)"
    const toNumber = (val) => {
      if (val === null || val === undefined || val === "") return 0;
      if (typeof val === "number") return isNaN(val) ? 0 : val;
      if (typeof val === "string") {
        const s = val
          .trim()
          .replace(/,/g, "")       // remove thousand separators
          .replace(/\s+/g, "")     // remove spaces
          .replace(/^\((.*)\)$/, "-$1"); // (123.45) -> -123.45
        const n = Number(s);
        return isNaN(n) ? 0 : n;
      }
      const n = Number(val);
      return isNaN(n) ? 0 : n;
    };

    const fmt = (n) =>
      toNumber(n).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

    const rowsHtml = (vendorData || [])
      .map(
        (tr) => `
          <tr>
            <td>${tr?.date ?? ""}</td>
            <td>${tr?.type ?? ""}</td>
            <td class="text-right">${fmt(tr?.amount)}</td>
            <td class="text-right">${fmt(tr?.balance)}</td>
          </tr>`
      )
      .join("");

    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <html>
        <head>
          <title>Vendor Ledger - ${selectedVendor?.label || "Report"}</title>
          <style>
            @media print {
              @page { margin: 0.5cm; }
              body { font-family: Arial, sans-serif; margin: 0; padding: 15px; }
            }
            body { font-family: Arial, sans-serif; margin: 0; padding: 15px; color: #000; }
            .print-header { text-align: center; margin-bottom: 20px; }
            .sub-title { margin: 6px 0 14px; font-weight: 600; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; }
            th { background: #f2f2f2; text-align: left; }
            .text-right { text-align: right; }
            .summary { width: 50%; margin-left: auto; }
            /* Ensure NO UI elements are printed even if present in page markup */
            .dataTables_filter, .dataTables_length, .dataTables_paginate, .dataTables_info,
            .pagination, input[type="search"], select[name*="length"],
            .red-cross-overlay, .red-cross-mark, .cross-container {
              display: none !important;
              visibility: hidden !important;
            }
          </style>
          <script>
            // Auto print then close
            window.onload = function () {
              setTimeout(function () {
                window.print();
                window.addEventListener('afterprint', function () { window.close(); });
              }, 200);
            };
          </script>
        </head>
        <body>
          <div class="print-header">
            <h2 class="text-uppercase">Nabil Restaurant</h2>
            <h4 class="sub-title">Supplier: ${selectedVendor?.label || ""}</h4>
            <div>Period: ${startDate || ""} to ${endDate || ""}</div>
          </div>

          <table>
            <thead>
              <tr>
                <th style="width: 20%;">Date</th>
                <th style="width: 30%;">Type</th>
                <th style="width: 25%;">Amount</th>
                <th style="width: 25%;">Balance</th>
              </tr>
            </thead>
            <tbody>
              ${rowsHtml}
            </tbody>
          </table>

          <table class="summary">
            <tbody>
              <tr>
                <td><strong>Previous Due</strong></td>
                <td class="text-right">+ ${fmt(summary?.previousDue)}</td>
              </tr>
              <tr>
                <td><strong>GRN Total</strong></td>
                <td class="text-right">+ ${fmt(summary?.grnTotal)}</td>
              </tr>
              <tr>
                <td><strong>Payment</strong></td>
                <td class="text-right">- ${fmt(summary?.payment)}</td>
              </tr>
              <tr>
                <td><strong>Return</strong></td>
                <td class="text-right">- ${fmt(summary?.return)}</td>
              </tr>
              <tr>
                <td><strong>Current Balance</strong></td>
                <td class="text-right"><strong>${fmt(summary?.balance)}</strong></td>
              </tr>
            </tbody>
          </table>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
  };

  return (
    <div>
      <IsLogin />
      <div>
        <IsLogin></IsLogin>
        <div>
          <div className="am-mainpanel">
            <div className="am-pagetitle">
              <h1 className="am-title">Supplier Ledger</h1>
            </div>


            <div className="am-pagebody">
              {alert && <Alert message={alert.message} type={alert.type} />}
              <section className="content">
                <div className="container-fluid">
                  <div className="row">
                    <div className="col-12">
                      <div className="card">
                        <div className="card-header">
                          <div className="row">
                            <div className="col-md-4" style={{zIndex: 999}}>
                              <div className="form-group">
                                <label>Select Vendor</label>
                                <Select
                                  options={vendorsList}
                                  value={selectedVendor}
                                  onChange={handleVendorChange}
                                  placeholder="Select Vendor"
                                  isClearable
                                />
                              </div>
                            </div>
                            <div className="col-md-3">
                              <div className="form-group">
                                <label>From Date</label>
                                <input
                                  type="date"
                                  className="form-control"
                                  value={startDate}
                                  onChange={(e) => setStartDate(e.target.value)}
                                />
                              </div>
                            </div>
                            <div className="col-md-3">
                              <div className="form-group">
                                <label>To Date</label>
                                <input
                                  type="date"
                                  className="form-control"
                                  value={endDate}
                                  onChange={(e) => setEndDate(e.target.value)}
                                />
                              </div>
                            </div>
                            <div className="col-md-2 d-flex align-items-center">
                              <button
                                className="btn btn-primary mr-2"
                                onClick={fetchLedgerData}
                                disabled={!selectedVendor}
                              >
                                Generate Report
                              </button>
                              {vendorData.length > 0 && (
                                <button
                                  className="btn btn-success"
                                  onClick={handlePrint}
                                  title="Print Ledger"
                                >
                                  <i className="fa fa-print"></i> Print
                                </button>
                              )}
                            </div>
                          </div>
                        </div>

                        {selectedVendor && (
                          <div className="card-body">
                            <div ref={printRef}>
                              <div className="text-center mb-4 print-header">
                                <h2 className="text-uppercase">Nabil Restaurant</h2>
                                <h4>Supplier: {selectedVendor.label}</h4>
                                <p>Period: {startDate} to {endDate}</p>
                              </div>

                              <ReactDataTable
                                cols={cols}
                                receiveData={vendorData}
                                isLoading={loading}
                                forVendor={true}
                              />

                              <div className="summary-section mt-4">
                                <div className="row">
                                  <div className="col-md-6 offset-md-6">
                                    <table className="table table-bordered summary-table">
                                      <tbody>
                                        <tr>
                                          <td><strong>Previous Due</strong></td>
                                          <td className="text-right">+ {summary.previousDue.toFixed(2)}</td>
                                        </tr>
                                        <tr>
                                          <td><strong>GRN Total</strong></td>
                                          <td className="text-right">+ {summary.grnTotal.toFixed(2)}</td>
                                        </tr>
                                        <tr>
                                          <td><strong>Payment</strong></td>
                                          <td className="text-right">- {summary.payment.toFixed(2)}</td>
                                        </tr>
                                        <tr>
                                          <td><strong>Return</strong></td>
                                          <td className="text-right">- {summary.return.toFixed(2)}</td>
                                        </tr>
                                        <tr className="bg-light">
                                          <td><strong>Current Balance</strong></td>
                                          <td className="text-right"><strong>{summary.balance.toFixed(2)}</strong></td>
                                        </tr>
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};



export default VendorLedger;


